import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  Card,
  Checkbox,
  Input,
  Select,
  Space,
  theme,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  EmailBlockType,
  IEmailBlock,
} from "../../../interfaces/emailTemplate.interface";
import {
  BLOCK_TYPE_LABELS,
  BLOCK_TYPES,
  newBlock,
} from "../emailTemplatesHelpers";

const { Text } = Typography;
const { TextArea } = Input;

interface BlockCardProps {
  id: string;
  block: IEmailBlock;
  onChange: (block: IEmailBlock) => void;
  onRemove: () => void;
}

const BlockCard: React.FC<BlockCardProps> = ({
  id,
  block,
  onChange,
  onRemove,
}) => {
  const { token } = theme.useToken();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    marginBottom: token.margin,
  };

  const patch = (partial: Partial<IEmailBlock>) =>
    onChange({ ...block, ...partial });

  const changeType = (type: EmailBlockType) => {
    if (type === block.type) return;
    const base = newBlock(type);
    const carriedText =
      block.text ?? block.items?.[0] ?? block.rows?.[0]?.value ?? "";
    if (type === "text" || type === "heading" || type === "code") {
      onChange({ ...base, text: carriedText });
    } else if (type === "button") {
      onChange({ ...base, text: carriedText, href: block.href ?? "" });
    } else if (type === "list") {
      onChange({
        ...base,
        ordered: block.ordered ?? false,
        items: block.items ?? (carriedText ? [carriedText] : [""]),
      });
    } else if (type === "table") {
      onChange({
        ...base,
        rows: block.rows ?? [{ label: "", value: carriedText }],
      });
    } else {
      onChange(base);
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        size="small"
        title={
          <Space>
            <span
              {...attributes}
              {...listeners}
              style={{ cursor: "grab", color: token.colorTextTertiary }}
            >
              <HolderOutlined />
            </span>
            <Select
              size="small"
              value={block.type}
              style={{ width: 140 }}
              onChange={changeType}
              options={BLOCK_TYPES.map((t) => ({
                label: BLOCK_TYPE_LABELS[t],
                value: t,
              }))}
            />
          </Space>
        }
        extra={
          <Button
            size="small"
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        }
      >
        {(block.type === "text" ||
          block.type === "heading" ||
          block.type === "code") && (
          <TextArea
            autoSize={{ minRows: block.type === "text" ? 2 : 1 }}
            placeholder={
              block.type === "code"
                ? "{{code}}"
                : "Text — supports {{vars}}, {{#if}} and inline <strong>/<a>"
            }
            value={block.text ?? ""}
            onChange={(e) => patch({ text: e.target.value })}
          />
        )}

        {block.type === "button" && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Input
              placeholder="Button label"
              value={block.text ?? ""}
              onChange={(e) => patch({ text: e.target.value })}
            />
            <Input
              placeholder="Link (href) — supports {{vars}}"
              value={block.href ?? ""}
              onChange={(e) => patch({ href: e.target.value })}
            />
          </Space>
        )}

        {block.type === "list" && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Checkbox
              checked={block.ordered ?? false}
              onChange={(e) => patch({ ordered: e.target.checked })}
            >
              Numbered list
            </Checkbox>
            {(block.items ?? []).map((item, index) => (
              <Space key={index} style={{ width: "100%" }} align="start">
                <TextArea
                  autoSize
                  style={{ width: 520 }}
                  value={item}
                  onChange={(e) => {
                    const items = [...(block.items ?? [])];
                    items[index] = e.target.value;
                    patch({ items });
                  }}
                />
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    patch({
                      items: (block.items ?? []).filter((_, i) => i !== index),
                    })
                  }
                />
              </Space>
            ))}
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => patch({ items: [...(block.items ?? []), ""] })}
            >
              Add item
            </Button>
          </Space>
        )}

        {block.type === "table" && (
          <Space direction="vertical" style={{ width: "100%" }}>
            {(block.rows ?? []).map((row, index) => {
              const updateRow = (partial: Partial<typeof row>) => {
                const rows = [...(block.rows ?? [])];
                rows[index] = { ...rows[index], ...partial };
                patch({ rows });
              };
              return (
                <Space key={index} style={{ width: "100%" }} align="start">
                  <Input
                    style={{ width: 200 }}
                    placeholder="Label"
                    value={row.label}
                    onChange={(e) => updateRow({ label: e.target.value })}
                  />
                  <Input
                    style={{ width: 220 }}
                    placeholder="Value"
                    value={row.value}
                    onChange={(e) => updateRow({ value: e.target.value })}
                  />
                  <Input
                    style={{ width: 160 }}
                    placeholder="Link (optional)"
                    value={row.href ?? ""}
                    onChange={(e) => updateRow({ href: e.target.value })}
                  />
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      patch({
                        rows: (block.rows ?? []).filter((_, i) => i !== index),
                      })
                    }
                  />
                </Space>
              );
            })}
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                patch({
                  rows: [...(block.rows ?? []), { label: "", value: "" }],
                })
              }
            >
              Add row
            </Button>
          </Space>
        )}

        {block.type === "code" && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Rendered large & centered (for verification codes).
          </Text>
        )}
      </Card>
    </div>
  );
};

export default BlockCard;
